// A project's brand kit: logo, colour, theme, typeface, buttons, loading screen and enquiry form,
// with saved styles at the top and a live preview (the real viewer in a frame, desktop or phone).
import {
  getProject, getPanoramas, draftUrl, uploadFile, update, getDraftManifest, syncPublished, openBilling, manifestBrand,
  getBrandKits, saveBrandKit, deleteBrandKit, loadGoogleFont, FONTS, DEFAULT_FONT,
  type ProjectBrand, type Studio, type BrandKit, type KitStyle, unreadLeads } from '../data';
import { tourUrl } from '@archviewvr/config';
import { html, raw, icon, shell, toast, setShellProject, confirmDialog, wireJumps, BRAND_OLD, $, $$ } from '../ui';

const SWATCHES = ['#0a70b5', '#1f2a37', '#0f3d5e', '#2f5d50', '#b4532a', '#a07a2c', '#6b3a5b', '#111111'];
const TEXT_LIGHT = ['#17212b', '#1f2a37', '#2b2622', '#0f3d5e'];
const TEXT_DARK = ['#eef2f6', '#ffffff', '#f3ece2', '#dcebf9'];
const DEFAULT_ACCENT = '#0a70b5';
const SHAPES = [{ id: 'round', label: 'Round' }, { id: 'soft', label: 'Soft square' }, { id: 'square', label: 'Square' }] as const;
const MAX_VIDEO = 25 * 1024 * 1024;

/** WCAG contrast of white text on a colour (buttons use white text). */
function contrastOnWhiteText(hex: string): number {
  const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  const L = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  return 1.05 / (L + 0.05);
}

/** Shrinks a picture to at most maxW wide and saves it as JPEG (loading backgrounds don't need more). */
async function shrinkToJpeg(file: File, maxW: number): Promise<Blob> {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, maxW / bmp.width);
  const c = document.createElement('canvas'); c.width = Math.round(bmp.width * scale); c.height = Math.round(bmp.height * scale);
  c.getContext('2d')!.drawImage(bmp, 0, 0, c.width, c.height);
  return new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('Couldn’t read that picture'))), 'image/jpeg', 0.86));
}

const ext = (name: string) => (name.split('.').pop() ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
const stamp = () => Date.now().toString(36);

export async function brandView(root: HTMLElement, studio: Studio, projectId: string, section?: string): Promise<void> {
  // the enquiry form has its own page now; older section links land in their group
  if (section === 'enquiries') { location.hash = `#/p/${projectId}/enquiries`; return; }
  if (section && BRAND_OLD[section]) section = BRAND_OLD[section];
  const page = shell(root, { studioName: studio.name, active: 'brand', project: { id: projectId }, sub: section });
  page.innerHTML = '<div class="loading-page">Loading brand kit</div>';
  const project = await getProject(projectId);
  if (!project) { page.innerHTML = '<div class="empty"><h2>Project not found</h2></div>'; return; }
  setShellProject(root, project, () => openBilling(project.id), unreadLeads(project.id));
  const [panos, kitsLoaded] = await Promise.all([getPanoramas(projectId), getBrandKits(studio.id).catch(() => [] as BrandKit[])]);
  let kits = kitsLoaded;
  const panoKeys = new Map(panos.map((p) => [p.id, p.base_key]));
  const b: ProjectBrand = structuredClone(project.brand ?? {});
  const lead = { enabled: !!project.lead_enabled, required: !!project.lead_required };
  let kitId = '';
  let dirty = false;
  /** a saved style was loaded, with its own enquiry settings, since the last save */
  let styleLead = false;
  let show: 'loading' | 'tour' | 'kiosk' = 'loading';
  let device: 'desktop' | 'phone' = 'desktop';

  /** The draft tour; a published project's preview names its live tour so it shows without the watermark. */
  const frameUrl = () => `/viewer/?draft=${project.id}${project.state === 'published' ? `&live=${project.slug}` : ''}${show === 'kiosk' ? '&kiosk=1' : ''}`;
  const url = (key?: string) => (key ? (/^https?:/.test(key) ? key : draftUrl(project.id, key)) : '');
  const accent = () => b.accent || DEFAULT_ACCENT;
  const font = () => b.font || DEFAULT_FONT;

  const kitOptions = () => html`<option value="">${kits.length ? 'Load a saved style…' : 'No saved styles yet'}</option>
    ${kits.map((k) => html`<option value="${k.id}" ${k.id === kitId ? raw('selected') : ''}>${k.name}</option>`)}`;

  page.innerHTML = html`
    <div class="page-head"><div><h1>Brand kit</h1><p class="sub">How this tour looks: your logo, colours, type and loading screen. “Powered by ArchviewVR” stays small in the corner.</p></div>
      <div class="btn-row"><span class="muted small" id="b-note"></span><button class="btn primary" id="b-save" disabled>Saved</button></div></div>

<i class="anchor" data-anchor="look"></i>
    <div class="kit-bar box">
      <div class="kit-l"><strong>Saved styles</strong><span class="muted small">Keep a style for each developer or builder you work with, then load it into any project.</span></div>
      <div class="kit-r">
        <select id="kit-pick" aria-label="Saved styles">${kitOptions()}</select>
        <button class="btn" id="kit-update" hidden>Update style</button>
        <button class="btn" id="kit-new">${icon('plus', 16)}Save as new style</button>
        <button class="link-btn" id="kit-del" hidden>Delete</button>
      </div>
    </div>

    <div class="brand-grid">
      <div class="brand-form">
        <section class="box brand-sec">
          <h2>Logo</h2>
          <p class="muted small">Shown top-left in the tour and on the loading screen. A PNG or SVG with a see-through background looks best.</p>
          <div class="logo-row">
            <div class="logo-box" id="logo-box"></div>
            <div class="btn-row">
              <label class="btn">${icon('image', 16)}<span id="logo-btn">Upload logo</span><input type="file" id="logo-file" accept="image/png,image/svg+xml,image/webp,image/jpeg" hidden></label>
              <button class="link-btn" id="logo-clear">Use the ArchviewVR logo</button>
            </div>
          </div>
          <div class="logo-row" id="dark-logo-row">
            <div class="logo-box dark" id="logo-dark-box"></div>
            <div class="btn-row">
              <label class="btn">${icon('image', 16)}<span id="logo-dark-btn">Upload a light logo</span><input type="file" id="logo-dark-file" accept="image/png,image/svg+xml,image/webp" hidden></label>
              <button class="link-btn" id="logo-dark-clear">Remove</button>
            </div>
            <p class="muted small" style="margin:0;flex-basis:100%">For the dark theme: a white or light version of your logo.</p>
          </div>
          <label class="toggle"><input type="checkbox" id="hide-logo"><span>Hide the logo <em>no logo on the loading screen or in the tour</em></span></label>
        </section>

        <section class="box brand-sec">
          <h2>Colour</h2>
          <p class="muted small">Used for buttons, highlights, hotspots and the loading bar.</p>
          <div class="swatches" id="accent-sw">${SWATCHES.map((c) => html`<button class="swatch" data-c="${c}" style="background:${c}" aria-label="Use ${c}"></button>`)}</div>
          <div class="colour-row">
            <input type="color" id="c-pick" value="${accent()}" aria-label="Pick a colour">
            <input id="c-hex" class="hex" value="${accent()}" maxlength="7" spellcheck="false" aria-label="Colour hex code">
          </div>
          <p class="warn-line" id="c-warn" hidden>White text is hard to read on this colour. Try a darker shade.</p>
        </section>

        <section class="box brand-sec">
          <h2>Theme</h2>
          <div class="seg" aria-label="Theme"><button data-theme="light">Light</button><button data-theme="dark">Dark</button></div>
          <div class="field"><span>Text colour</span>
            <div class="swatches" id="text-sw"></div>
            <div class="colour-row"><input type="color" id="t-pick" aria-label="Pick a text colour"><input id="t-hex" class="hex" maxlength="7" spellcheck="false" placeholder="Default" aria-label="Text colour hex code"></div>
          </div>
        </section>

        <section class="box brand-sec">
          <h2>Typeface</h2>
          <select id="font-pick" aria-label="Typeface">
            ${FONTS.map((f) => html`<option value="${f}">${f}${f === DEFAULT_FONT ? ' (default)' : ''}</option>`)}
            <option value="custom">Upload your own…</option>
          </select>
          <label class="btn font-up" id="font-up" hidden>${icon('up', 16)}<span id="font-btn">Upload a font file</span><input type="file" id="font-file" accept=".woff2,.woff,.ttf,.otf" hidden></label>
          <p class="font-sample" id="font-sample">Welcome home. Walk through every room.</p>
        </section>

        <section class="box brand-sec">
          <h2>Buttons</h2>
          <div class="shapes">${SHAPES.map((s) => html`<button class="shape-opt" data-shape="${s.id}"><i class="shape-${s.id}"></i>${s.label}</button>`)}</div>
        </section>

        <i class="anchor" data-anchor="loading"></i><section class="box brand-sec">
          <h2>Loading screen</h2>
          <label class="field"><span>Title</span><input id="t-title" value="${b.title ?? ''}" placeholder="${project.name}"></label>
          <label class="field"><span>Tagline <em class="muted">optional</em></span><input id="t-tag" value="${b.tagline ?? ''}" placeholder="Enter a short line, like an address or opening hours" maxlength="90"></label>
          <div class="field"><span>Background</span>
            <div class="bg-opts">
              <button class="bg-opt" data-bg="none"><span class="bg-plain"></span>Plain</button>
              ${panos.slice(0, 10).map((p) => html`<button class="bg-opt" data-bg="render" data-pano="${p.id}"><img src="${draftUrl(project.id, `thumbs/${p.id}.jpg`)}" alt="" loading="lazy">${p.room || p.name}</button>`)}
              <label class="bg-opt upload" data-kind="image"><span class="bg-plain">${icon('image', 18)}</span>Picture<input type="file" id="bg-file" accept="image/*" hidden></label>
              <label class="bg-opt upload" data-kind="video"><span class="bg-plain">${icon('video', 18)}</span>Video loop<input type="file" id="vid-file" accept="video/mp4,video/webm" hidden></label>
            </div>
            <em class="muted small hint">A video plays muted on a loop. Keep it short: under 25 MB, MP4.</em>
          </div>
          <label class="toggle"><input type="checkbox" id="explore"><span>Explore button <em>Wait on the loading screen until they press Explore, then fade slowly into the tour.</em></span></label>
        </section>

        <i class="anchor" data-anchor="extras"></i><section class="box brand-sec">
          <h2>Music</h2>
          <div class="music-row" id="music-row"></div>
        </section>

        <section class="box brand-sec">
          <h2>Take this tour home</h2>
          <label class="toggle"><input type="checkbox" id="qr-on"><span>QR code button in the tour <em>On computers, buyers can scan it to carry on exploring on their phone.</em></span></label>
        </section>

        <section class="box brand-sec" id="kiosk-sec">
          <h2>Kiosk mode</h2>
          <p class="muted small">For a screen in your display suite. When nobody has touched it for a while, the tour plays itself room by room. A touch hands control to the buyer.</p>
          <div class="kiosk-grid">
            <label class="field"><span>Start playing after</span><select id="k-idle">${[[30, '30 seconds'], [60, '1 minute'], [120, '2 minutes'], [300, '5 minutes']].map(([v, l]) => html`<option value="${v}">${l}</option>`)}</select></label>
            <label class="field"><span>Time in each room</span><select id="k-dwell">${[[6, '6 seconds'], [9, '9 seconds'], [12, '12 seconds'], [18, '18 seconds']].map(([v, l]) => html`<option value="${v}">${l}</option>`)}</select></label>
          </div>
          <label class="toggle"><input type="checkbox" id="k-qr"><span>Show “Take this tour home” <em>A QR code in the corner so buyers can leave with the tour on their phone.</em></span></label>
          <div class="field"><span>Kiosk link</span>
            ${project.state === 'published'
              ? html`<div class="copy-row"><input readonly id="k-link" value="${tourUrl(project.slug)}?kiosk=1"><button class="btn" id="k-copy">Copy</button><a class="btn" href="${tourUrl(project.slug)}?kiosk=1" target="_blank" rel="noopener">Open</a></div>
                <em class="muted small hint">Open it on the display screen's browser and make it full screen (F11 on Windows).</em>`
              : html`<em class="muted small hint">You get a kiosk link once the tour is published. Use Kiosk in the preview to try it now.</em>`}
          </div>
        </section>

        <p class="muted small brand-moved">The enquiry form and who it emails are under <a href="#/p/${project.id}/enquiries">Enquiries</a>.</p>
      </div>

      <div class="brand-preview">
        <div class="pv-bar-top">
          <div class="seg small" aria-label="Show"><button data-show="loading">Loading screen</button><button data-show="tour">In the tour</button><button data-show="kiosk">Kiosk</button></div>
          <div class="seg small" aria-label="Device"><button data-device="desktop">${icon('monitor', 15)}Desktop</button><button data-device="phone">${icon('phone', 15)}Phone</button></div>
        </div>
        <div class="pv-stage" id="pv-stage"><div class="pv-device" id="pv-device"><iframe id="pv-tour" src="${frameUrl()}" title="Tour preview"></iframe></div></div>
        <p class="muted small" style="margin:0">${panos.length ? 'This is the real tour. Try the Explore button or the form.' : 'Upload and save renders to see the tour here.'}</p>
      </div>
    </div>`.html;

  // ------------------------------------------------------------ preview
  const frame = $<HTMLIFrameElement>('#pv-tour', page);
  const absBrand = () => {
    const m = manifestBrand(b, panoKeys);
    for (const k of ['logo', 'logoDark', 'loadingBackground', 'loadingVideo', 'fontUrl', 'music'] as const) if (m[k]) m[k] = url(m[k]);
    return m;
  };
  const sendBrand = () => frame.contentWindow?.postMessage({ avr: 'brand', brand: absBrand(), name: project.name, lead, show: show === 'kiosk' ? 'tour' : show }, location.origin);
  /** Kiosk preview is the viewer opened in kiosk mode; switching reloads the frame. */
  const syncFrameSrc = () => {
    const want = frameUrl();
    if (frame.getAttribute('src') !== want) frame.setAttribute('src', want);
  };
  const onReady = (e: MessageEvent) => {
    if (e.origin !== location.origin || e.source !== frame.contentWindow || e.data?.avr !== 'ready') return;
    sendBrand();
  };
  addEventListener('message', onReady);
  // also as soon as the frame loads, so the loading screen wears the brand while the tour loads
  frame.addEventListener('load', () => sendBrand());

  const fit = () => {
    const stage = $('#pv-stage', page), dev = $('#pv-device', page);
    // tall enough to really see the tour: 80% of the window on computers, 90% on phones
    const small = innerWidth <= 760;
    const tall = innerHeight * (small ? 0.9 : 0.8);
    const sw = stage.clientWidth;
    let w: number, h: number, scale: number;
    if (device === 'phone') { [w, h] = [390, 780]; scale = Math.min(1, sw / w, tall / h); }
    else if (small) { w = sw; h = tall; scale = 1; } // on a phone, the desktop view is just the full-size tour
    else { scale = Math.min(1, sw / 1280); w = sw / scale; h = tall / scale; }
    dev.style.width = `${w}px`; dev.style.height = `${h}px`; dev.style.transform = `scale(${scale})`;
    dev.style.left = `${Math.max(0, (stage.clientWidth - w * scale) / 2)}px`;
    stage.style.height = `${h * scale}px`;
    stage.classList.toggle('phone', device === 'phone');
  };
  const ro = new ResizeObserver(fit); ro.observe($('#pv-stage', page));
  $$<HTMLButtonElement>('[data-show]', page).forEach((x) => (x.onclick = () => { show = x.dataset.show as typeof show; syncFrameSrc(); draw(); }));
  $$<HTMLButtonElement>('[data-device]', page).forEach((x) => (x.onclick = () => { device = x.dataset.device as typeof device; draw(); fit(); }));

  // ------------------------------------------------------------ drawing
  const press = (sel: string, on: (el: HTMLElement) => boolean) => $$<HTMLElement>(sel, page).forEach((el) => el.setAttribute('aria-pressed', String(on(el))));
  const draw = () => {
    $('#logo-box', page).innerHTML = html`<img src="${url(b.logo) || '/archviewvr-logo.svg'}" alt="Logo">`.html;
    $('#dark-logo-row', page).hidden = b.theme !== 'dark';
    $('#logo-dark-box', page).innerHTML = html`<img src="${url(b.logoDark) || url(b.logo) || '/archviewvr-logo-white.svg'}" alt="Logo on dark">`.html;
    $('#logo-dark-btn', page).textContent = b.logoDark ? 'Replace light logo' : 'Upload a light logo';
    $('#logo-dark-clear', page).hidden = !b.logoDark;
    $<HTMLInputElement>('#hide-logo', page).checked = !!b.hideLogo;
    $('#logo-btn', page).textContent = b.logo ? 'Replace logo' : 'Upload logo';
    $('#logo-clear', page).hidden = !b.logo;
    press('#accent-sw .swatch', (s) => s.dataset.c!.toLowerCase() === accent().toLowerCase());
    $<HTMLInputElement>('#c-pick', page).value = accent();
    if (document.activeElement !== $('#c-hex', page)) $<HTMLInputElement>('#c-hex', page).value = accent();
    $('#c-warn', page).hidden = contrastOnWhiteText(accent()) >= 3;
    // theme and text colour
    press('[data-theme]', (x) => x.dataset.theme === (b.theme ?? 'light'));
    const texts = b.theme === 'dark' ? TEXT_DARK : TEXT_LIGHT;
    $('#text-sw', page).innerHTML = html`<button class="swatch def" data-t="" aria-label="Theme default" title="Theme default">A</button>${texts.map((c) => html`<button class="swatch" data-t="${c}" style="background:${c}" aria-label="Use ${c}"></button>`)}`.html;
    $$<HTMLButtonElement>('#text-sw .swatch', page).forEach((s) => (s.onclick = () => { b.text = s.dataset.t || undefined; touch(); }));
    press('#text-sw .swatch', (s) => (s.dataset.t || '') === (b.text ?? ''));
    $<HTMLInputElement>('#t-pick', page).value = b.text || texts[0];
    if (document.activeElement !== $('#t-hex', page)) $<HTMLInputElement>('#t-hex', page).value = b.text ?? '';
    // typeface
    $<HTMLSelectElement>('#font-pick', page).value = font();
    $('#font-up', page).hidden = font() !== 'custom';
    $('#font-btn', page).textContent = b.fontFile ? 'Replace font file' : 'Upload a font file';
    const sample = $('#font-sample', page);
    if (font() === 'custom' && b.fontFile) {
      new FontFace('BrandPreview', `url(${JSON.stringify(url(b.fontFile))})`).load()
        .then((ff) => { document.fonts.add(ff); sample.style.fontFamily = '"BrandPreview", sans-serif'; }).catch(() => {});
    } else if (font() !== 'custom') { loadGoogleFont(font()); sample.style.fontFamily = `"${font()}", sans-serif`; }
    // buttons
    press('.shape-opt', (x) => x.dataset.shape === (b.shape ?? 'round'));
    // loading screen
    press('.bg-opt[data-bg]', (o) => (o.dataset.bg === 'none' ? !b.background || b.background.kind === 'none' : b.background?.kind === 'render' && b.background.pano === o.dataset.pano));
    press('.bg-opt.upload', (o) => b.background?.kind === o.dataset.kind);
    $<HTMLInputElement>('#explore', page).checked = !!b.explore;
    drawMusic();
    // take home and kiosk
    $<HTMLInputElement>('#qr-on', page).checked = !!b.qr;
    $<HTMLSelectElement>('#k-idle', page).value = String(b.kiosk?.idle ?? 60);
    $<HTMLSelectElement>('#k-dwell', page).value = String(b.kiosk?.dwell ?? 9);
    $<HTMLInputElement>('#k-qr', page).checked = b.kiosk?.qr ?? true;
    // saved styles
    $('#kit-update', page).hidden = !kitId;
    $('#kit-del', page).hidden = !kitId;
    // preview
    press('[data-show]', (x) => x.dataset.show === show);
    press('[data-device]', (x) => x.dataset.device === device);
    sendBrand();
  };
  const touch = () => {
    dirty = true;
    const s = $<HTMLButtonElement>('#b-save', page); s.disabled = false; s.textContent = 'Save';
    $('#b-note', page).textContent = 'Unsaved changes';
    draw();
  };
  const upload = async (key: string, body: Blob, okMsg: string): Promise<boolean> => {
    try { await uploadFile(project.id, key, body); toast(okMsg); return true; }
    catch (err) { toast(err instanceof Error ? err.message : 'Upload failed', 'error', 7000); return false; }
  };

  // ------------------------------------------------------------ logo and colour
  $<HTMLInputElement>('#logo-file', page).onchange = async (e) => {
    const f = (e.target as HTMLInputElement).files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast('Logos need to be under 5 MB', 'error'); return; }
    const x = ({ 'image/png': 'png', 'image/svg+xml': 'svg', 'image/webp': 'webp', 'image/jpeg': 'jpg' } as Record<string, string>)[f.type];
    if (!x) { toast('Use a PNG, SVG, WebP or JPG logo', 'error'); return; }
    const key = `brand/logo-${stamp()}.${x}`;
    if (await upload(key, f, 'Logo uploaded. Save to use it.')) { b.logo = key; touch(); }
  };
  $('#logo-clear', page).onclick = () => { delete b.logo; touch(); };
  $<HTMLInputElement>('#logo-dark-file', page).onchange = async (e) => {
    const f = (e.target as HTMLInputElement).files?.[0]; if (!f) return;
    const x = ({ 'image/png': 'png', 'image/svg+xml': 'svg', 'image/webp': 'webp' } as Record<string, string>)[f.type];
    if (!x || f.size > 5 * 1024 * 1024) { toast('Use a PNG, SVG or WebP under 5 MB', 'error'); return; }
    const key = `brand/logo-dark-${stamp()}.${x}`;
    if (await upload(key, f, 'Light logo uploaded. Save to use it.')) { b.logoDark = key; touch(); }
  };
  $('#logo-dark-clear', page).onclick = () => { delete b.logoDark; touch(); };
  $<HTMLInputElement>('#hide-logo', page).onchange = (e) => { if ((e.target as HTMLInputElement).checked) b.hideLogo = true; else delete b.hideLogo; touch(); };
  $$<HTMLButtonElement>('#accent-sw .swatch', page).forEach((s) => (s.onclick = () => { b.accent = s.dataset.c!; touch(); }));
  $<HTMLInputElement>('#c-pick', page).oninput = (e) => { b.accent = (e.target as HTMLInputElement).value; touch(); };
  const hexInput = (v: string) => { v = v.trim(); if (v && !v.startsWith('#')) v = `#${v}`; return /^#[0-9a-f]{6}$/i.test(v) ? v.toLowerCase() : null; };
  $<HTMLInputElement>('#c-hex', page).oninput = (e) => { const v = hexInput((e.target as HTMLInputElement).value); if (v) { b.accent = v; touch(); } };

  // ------------------------------------------------------------ theme, type, buttons
  $$<HTMLButtonElement>('[data-theme]', page).forEach((x) => (x.onclick = () => {
    const t = x.dataset.theme as 'light' | 'dark';
    if ((b.theme ?? 'light') === t) return;
    b.theme = t === 'dark' ? 'dark' : undefined;
    b.text = undefined; // a light text colour on a light theme would vanish
    touch();
  }));
  $<HTMLInputElement>('#t-pick', page).oninput = (e) => { b.text = (e.target as HTMLInputElement).value; touch(); };
  $<HTMLInputElement>('#t-hex', page).oninput = (e) => {
    const v0 = (e.target as HTMLInputElement).value;
    if (!v0.trim()) { b.text = undefined; touch(); return; }
    const v = hexInput(v0); if (v) { b.text = v; touch(); }
  };
  $<HTMLSelectElement>('#font-pick', page).onchange = (e) => {
    const v = (e.target as HTMLSelectElement).value;
    b.font = v === DEFAULT_FONT ? undefined : v;
    touch();
    if (v === 'custom' && !b.fontFile) $<HTMLInputElement>('#font-file', page).click();
  };
  $<HTMLInputElement>('#font-file', page).onchange = async (e) => {
    const f = (e.target as HTMLInputElement).files?.[0]; if (!f) return;
    const x = ext(f.name);
    if (!['woff2', 'woff', 'ttf', 'otf'].includes(x)) { toast('Use a WOFF2, WOFF, TTF or OTF font file', 'error'); return; }
    if (f.size > 3 * 1024 * 1024) { toast('Font files need to be under 3 MB', 'error'); return; }
    const key = `brand/font-${stamp()}.${x}`;
    if (await upload(key, f, 'Font uploaded. Check your font licence covers websites.')) { b.font = 'custom'; b.fontFile = key; touch(); }
  };
  $$<HTMLButtonElement>('.shape-opt', page).forEach((x) => (x.onclick = () => { b.shape = x.dataset.shape === 'round' ? undefined : (x.dataset.shape as ProjectBrand['shape']); touch(); }));

  // ------------------------------------------------------------ loading screen and enquiry form
  $<HTMLInputElement>('#t-title', page).oninput = (e) => { b.title = (e.target as HTMLInputElement).value.trim() || undefined; touch(); };
  $<HTMLInputElement>('#t-tag', page).oninput = (e) => { b.tagline = (e.target as HTMLInputElement).value.trim() || undefined; touch(); };
  $$<HTMLButtonElement>('.bg-opt[data-bg]', page).forEach((o) => (o.onclick = () => {
    b.background = o.dataset.bg === 'none' ? { kind: 'none' } : { kind: 'render', pano: o.dataset.pano! }; show = 'loading'; touch();
  }));
  $<HTMLInputElement>('#bg-file', page).onchange = async (e) => {
    const f = (e.target as HTMLInputElement).files?.[0]; if (!f) return;
    try {
      const jpg = await shrinkToJpeg(f, 2560);
      const key = `brand/loading-${stamp()}.jpg`;
      if (await upload(key, jpg, 'Picture uploaded. Save to use it.')) { b.background = { kind: 'image', key }; show = 'loading'; touch(); }
    } catch (err) { toast(err instanceof Error ? err.message : 'Couldn’t read that picture', 'error', 7000); }
  };
  $<HTMLInputElement>('#vid-file', page).onchange = async (e) => {
    const f = (e.target as HTMLInputElement).files?.[0]; if (!f) return;
    const x = f.type === 'video/webm' ? 'webm' : f.type === 'video/mp4' ? 'mp4' : '';
    if (!x) { toast('Use an MP4 or WebM video', 'error'); return; }
    if (f.size > MAX_VIDEO) { toast('Keep the video under 25 MB. A 10 to 20 second loop is plenty.', 'error', 7000); return; }
    const key = `brand/intro-${stamp()}.${x}`;
    toast('Uploading video…', 'info', 60000);
    if (await upload(key, f, 'Video uploaded. Save to use it.')) { b.background = { kind: 'video', key }; show = 'loading'; touch(); }
  };
  $<HTMLInputElement>('#explore', page).onchange = (e) => { b.explore = (e.target as HTMLInputElement).checked || undefined; show = 'loading'; touch(); };

  // ------------------------------------------------------------ take home and kiosk
  $<HTMLInputElement>('#qr-on', page).onchange = (e) => { b.qr = (e.target as HTMLInputElement).checked || undefined; if (show === 'loading') show = 'tour'; touch(); };
  const setKiosk = (k: Partial<NonNullable<ProjectBrand['kiosk']>>) => { b.kiosk = { ...(b.kiosk ?? {}), ...k }; touch(); };
  $<HTMLSelectElement>('#k-idle', page).onchange = (e) => setKiosk({ idle: +(e.target as HTMLSelectElement).value });
  $<HTMLSelectElement>('#k-dwell', page).onchange = (e) => setKiosk({ dwell: +(e.target as HTMLSelectElement).value });
  $<HTMLInputElement>('#k-qr', page).onchange = (e) => setKiosk({ qr: (e.target as HTMLInputElement).checked });
  const kc = page.querySelector<HTMLButtonElement>('#k-copy');
  if (kc) kc.onclick = async () => {
    try { await navigator.clipboard.writeText($<HTMLInputElement>('#k-link', page).value); toast('Kiosk link copied'); }
    catch { toast('Couldn’t copy. Select the link and copy it instead.', 'error'); }
  };

  // ------------------------------------------------------------ music
  let musicDrawn: string | null = null;
  function drawMusic(): void {
    if (musicDrawn === (b.music ?? '')) return; // keep the player as it is while other things change
    musicDrawn = b.music ?? '';
    const row = $('#music-row', page);
    row.innerHTML = b.music
      ? html`<audio controls loop src="${url(b.music)}"></audio><button class="link-btn" id="music-clear">Remove music</button>
        <em class="muted small">Buyers get a music button in the tour. It stays off until they turn it on.</em>`.html
      : html`<label class="btn">${icon('up', 16)}Upload music<input type="file" id="music-file" accept="audio/mpeg,audio/mp4,.mp3,.m4a" hidden></label>
        <em class="muted small">An MP3 loop under 10 MB. Buyers get a music button in the tour, off until they turn it on. Make sure you have the rights to the track.</em>`.html;
    row.querySelector('#music-clear')?.addEventListener('click', () => { delete b.music; touch(); });
    const f = row.querySelector<HTMLInputElement>('#music-file');
    if (f) f.onchange = async () => {
      const file = f.files?.[0]; if (!file) return;
      const x = /\.m4a$/i.test(file.name) || file.type === 'audio/mp4' ? 'm4a' : 'mp3';
      if (file.size > 10 * 1024 * 1024) { toast('Keep the music under 10 MB', 'error'); return; }
      toast('Uploading music…', 'info', 30000);
      const key = `brand/music-${stamp()}.${x}`;
      if (await upload(key, file, 'Music uploaded. Save to use it.')) { b.music = key; touch(); }
    };
  }

  // ------------------------------------------------------------ saved styles
  /** This project's style with full file addresses, so another project can copy the files. */
  const toKit = (): KitStyle => {
    const { title: _t, background, logo, logoDark, fontFile, music, ...rest } = b; void _t;
    const k: KitStyle = { ...rest, lead: { ...lead } };
    if (logo) k.logo = url(logo);
    if (logoDark) k.logoDark = url(logoDark);
    if (music) k.music = url(music);
    if (fontFile) k.fontFile = url(fontFile);
    if (background?.kind === 'image') k.background = { kind: 'image', url: url(background.key) };
    else if (background?.kind === 'video') k.background = { kind: 'video', url: url(background.key) };
    else if (!background || background.kind === 'none') k.background = { kind: 'none' };
    return k;
  };
  /** Copies a style's file into this project (unless it's already here) and returns its key. */
  const own = async (src: string, prefix: string): Promise<string> => {
    const mine = draftUrl(project.id, '');
    if (src.startsWith(mine)) return src.slice(mine.length);
    const res = await fetch(src);
    if (!res.ok) throw new Error('A file in this style is missing');
    const key = `brand/${prefix}-${stamp()}.${ext(new URL(src).pathname) || 'bin'}`;
    await uploadFile(project.id, key, await res.blob());
    return key;
  };
  const applyKit = async (k: BrandKit) => {
    const d = k.data;
    toast(`Loading ${k.name}…`, 'info', 30000);
    try {
      const next: ProjectBrand = { title: b.title, tagline: d.tagline ?? b.tagline };
      if (b.background?.kind === 'render') next.background = b.background; // this project's renders stay
      for (const key of ['accent', 'theme', 'text', 'shape', 'font', 'explore'] as const) if (d[key] !== undefined) (next as Record<string, unknown>)[key] = d[key];
      if (d.logo) next.logo = await own(d.logo, 'logo');
      if (d.logoDark) next.logoDark = await own(d.logoDark, 'logo-dark');
      if (d.music) next.music = await own(d.music, 'music');
      if (d.fontFile) next.fontFile = await own(d.fontFile, 'font');
      if (d.background?.kind === 'image') next.background = { kind: 'image', key: await own(d.background.url, 'loading') };
      else if (d.background?.kind === 'video') next.background = { kind: 'video', key: await own(d.background.url, 'intro') };
      else if (d.background?.kind === 'none' && next.background?.kind !== 'render') next.background = { kind: 'none' };
      for (const key of Object.keys(b) as Array<keyof ProjectBrand>) delete b[key];
      Object.assign(b, next);
      if (d.lead) { Object.assign(lead, d.lead); styleLead = true; }
      kitId = k.id;
      touch();
      toast(`${k.name} loaded. Save to use it on this tour.`);
    } catch (err) { toast(err instanceof Error ? err.message : 'Couldn’t load that style', 'error', 7000); }
  };
  const refreshKits = () => { $('#kit-pick', page).innerHTML = kitOptions().html; draw(); };
  $<HTMLSelectElement>('#kit-pick', page).onchange = async (e) => {
    const id = (e.target as HTMLSelectElement).value;
    const k = kits.find((x) => x.id === id);
    if (k) await applyKit(k); else { kitId = ''; draw(); }
  };
  $('#kit-new', page).onclick = async () => {
    const name = prompt('Name this style, for example the developer or builder it’s for:', '')?.trim();
    if (!name) return;
    try {
      const k = await saveBrandKit(studio.id, name.slice(0, 60), toKit());
      kits = [...kits, k]; kitId = k.id; refreshKits();
      toast(`Saved “${k.name}”. Load it into any project from here.`);
    } catch (err) { toast(err instanceof Error ? err.message : 'Couldn’t save the style', 'error', 7000); }
  };
  $('#kit-update', page).onclick = async () => {
    const k = kits.find((x) => x.id === kitId); if (!k) return;
    try {
      const saved = await saveBrandKit(studio.id, k.name, toKit(), k.id);
      kits = kits.map((x) => (x.id === k.id ? saved : x)); refreshKits();
      toast(`Updated “${k.name}”`);
    } catch (err) { toast(err instanceof Error ? err.message : 'Couldn’t update the style', 'error', 7000); }
  };
  $('#kit-del', page).onclick = async () => {
    const k = kits.find((x) => x.id === kitId); if (!k) return;
    if (!(await confirmDialog({ title: `Delete “${k.name}”?`, body: 'Tours already using it keep their look.', confirm: 'Delete style', danger: true }))) return;
    try { await deleteBrandKit(k.id); kits = kits.filter((x) => x.id !== k.id); kitId = ''; refreshKits(); toast('Style deleted'); }
    catch (err) { toast(err instanceof Error ? err.message : 'Couldn’t delete the style', 'error', 7000); }
  };

  // ------------------------------------------------------------ save: the project row, then the tour file (and the live tour)
  $<HTMLButtonElement>('#b-save', page).onclick = async () => {
    const s = $<HTMLButtonElement>('#b-save', page); s.disabled = true; s.textContent = 'Saving…';
    try {
      // the Enquire tab is set on the Enquiries page: keep whatever it saved meanwhile (a loaded style sets its own)
      const fresh = await getProject(project.id);
      if (fresh && !styleLead) { b.enquireTab = fresh.brand?.enquireTab; b.enquireLabel = fresh.brand?.enquireLabel; Object.assign(lead, { enabled: !!fresh.lead_enabled, required: !!fresh.lead_required }); }
      await update('projects', `id=eq.${project.id}`, { brand: b, lead_enabled: lead.enabled, lead_required: lead.required });
      project.brand = structuredClone(b); project.lead_enabled = lead.enabled; project.lead_required = lead.required;
      styleLead = false;
      if (panos.length) {
        const man = await getDraftManifest(project.id);
        man.brand = manifestBrand(b, panoKeys);
        man.lead = { enabled: lead.enabled, required: lead.required };
        await uploadFile(project.id, 'manifest.json', new Blob([JSON.stringify(man)], { type: 'application/json' }));
        if (project.state === 'published') await syncPublished(project.id);
      }
      dirty = false; s.textContent = 'Saved';
      $('#b-note', page).textContent = project.state === 'published' ? 'Saved. The live tour is updated.' : 'Saved';
      toast('Brand kit saved');
    } catch (err) { s.disabled = false; s.textContent = 'Save'; toast(`Couldn’t save: ${err instanceof Error ? err.message : 'unknown error'}`, 'error', 8000); }
  };

  const guard = (e: BeforeUnloadEvent) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
  addEventListener('beforeunload', guard);
  addEventListener('hashchange', () => { removeEventListener('beforeunload', guard); removeEventListener('message', onReady); ro.disconnect(); }, { once: true });
  draw(); fit();
  wireJumps(root, page, section);
}
